import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit3, Trash2, X, Maximize2, Minimize2 } from 'lucide-react';
import FileAttachmentSection from './FileAttachmentSection';
import { fileAttachmentUtils } from '../utils/fileAttachmentUtils';

export const normalizeNoteContent = (content = '') => {
  if (!content) return '';

  const normalizedMarkup = content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<li>/gi, '\n- ')
    .replace(/<\/(li|tr)>/gi, '\n')
    .replace(/&nbsp;/gi, ' ');

  if (typeof document !== 'undefined' && document.createElement) {
    const temp = document.createElement('div');
    temp.innerHTML = normalizedMarkup;
    return (
      temp.textContent
        ?.replace(/\u00a0/g, ' ')
        .replace(/[\t\f\r]+/g, ' ')
        .replace(/\s+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim() || ''
    );
  }

  return normalizedMarkup
    .replace(/<[^>]+>/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/[\t\f\r]+/g, ' ')
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const NotesModal = ({
  isOpen,
  contactId,
  contactType,
  currentNotes,
  onClose,
  onSave,
  openNewNoteForm = false
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingContent, setEditingContent] = useState('');
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);
  const [isNewNoteExpanded, setIsNewNoteExpanded] = useState(false);
  const [newNoteAttachments, setNewNoteAttachments] = useState([]);
  const [uploadingNewAttachments, setUploadingNewAttachments] = useState(false);
  const [editingAttachments, setEditingAttachments] = useState([]);
  const [editingRemovedAttachmentIds, setEditingRemovedAttachmentIds] = useState([]);
  const [uploadingEditAttachments, setUploadingEditAttachments] = useState(false);

  const lowPriorityTitles = new Set(['email received', 'email sent', 'note title']);

  const enhanceAttachments = (attachments = []) =>
    (attachments || []).map(att => ({
      ...att,
      filename: att?.filename || att?.originalName,
      originalName: att?.originalName || att?.filename,
      persisted: att?.persisted !== false
    }));

  const enhanceNote = (note) => ({
    ...note,
    attachments: enhanceAttachments(note?.attachments || [])
  });

  const sortNotesForDisplay = (noteList = []) => {
    return [...noteList].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const cleanupTemporaryAttachments = async (attachments = []) => {
    const tempAttachments = (attachments || []).filter(att => att && att.persisted !== true);
    await Promise.all(
      tempAttachments.map(att =>
        fileAttachmentUtils.deleteFile(att.id).catch(err => {
          console.error('Failed to cleanup attachment:', err);
        })
      )
    );
  };

  const deleteAttachmentFilesByIds = async (ids = []) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    await Promise.all(
      uniqueIds.map(id =>
        fileAttachmentUtils.deleteFile(id).catch(err => {
          console.error('Failed to delete attachment:', err);
        })
      )
    );
  };

  const deleteAttachmentFiles = async (attachments = []) => {
    await deleteAttachmentFilesByIds((attachments || []).map(att => att.id));
  };

  const handleFileUpload = async (files, currentAttachments, setAttachments, setUploading) => {
    const fileArray = Array.from(files || []);
    if (!fileArray.length) return;

    setUploading(true);
    const pendingAttachments = [];

    for (const file of fileArray) {
      const validation = fileAttachmentUtils.validateFile(file);
      if (!validation.isValid) {
        alert(`File "${file.name}" is invalid:\n${validation.errors.join('\n')}`);
        continue;
      }

      const sizeCheck = fileAttachmentUtils.isWithinSizeLimit([
        ...currentAttachments,
        ...pendingAttachments,
        { size: file.size }
      ]);
      if (!sizeCheck.isValid) {
        alert(
          `Adding "${file.name}" would exceed the 25MB limit.\n` +
          `Current total: ${fileAttachmentUtils.formatFileSize(sizeCheck.totalSize - file.size)}\n` +
          `File size: ${fileAttachmentUtils.formatFileSize(file.size)}`
        );
        continue;
      }

      try {
        const uploadedFile = await fileAttachmentUtils.uploadFile(file);
        const attachmentData = {
          ...fileAttachmentUtils.createAttachmentData(uploadedFile),
          persisted: false
        };
        pendingAttachments.push(attachmentData);
      } catch (error) {
        console.error('Failed to upload file:', error);
        alert(`Failed to upload "${file.name}": ${error.message}`);
      }
    }

    if (pendingAttachments.length) {
      setAttachments(prev => [...prev, ...pendingAttachments]);
    }
    setUploading(false);
  };

  const handleNewNoteFileSelect = async (event) => {
    await handleFileUpload(event?.target?.files, newNoteAttachments, setNewNoteAttachments, setUploadingNewAttachments);
    if (event?.target) {
      event.target.value = '';
    }
  };

  const handleRemoveNewNoteAttachment = async (attachmentId) => {
    const attachment = newNoteAttachments.find(att => att.id === attachmentId);
    if (!attachment) return;

    try {
      await fileAttachmentUtils.deleteFile(attachmentId);
    } catch (error) {
      console.error('Failed to remove attachment:', error);
    }

    setNewNoteAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const handleEditFileSelect = async (event) => {
    await handleFileUpload(event?.target?.files, editingAttachments, setEditingAttachments, setUploadingEditAttachments);
    if (event?.target) {
      event.target.value = '';
    }
  };

  const handleRemoveEditingAttachment = async (attachmentId) => {
    const attachment = editingAttachments.find(att => att.id === attachmentId);
    if (!attachment) return;

    if (attachment.persisted) {
      setEditingAttachments(prev => prev.filter(att => att.id !== attachmentId));
      setEditingRemovedAttachmentIds(prev => Array.from(new Set([...prev, attachmentId])));
      return;
    }

    try {
      await fileAttachmentUtils.deleteFile(attachmentId);
    } catch (error) {
      console.error('Failed to remove attachment:', error);
    }

    setEditingAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const cancelNewNoteForm = async () => {
    await cleanupTemporaryAttachments(newNoteAttachments);
    setNewNoteAttachments([]);
    setNewNoteTitle('');
    setNewNoteContent('');
    setShowNewNoteForm(false);
    setIsNewNoteExpanded(false);
  };

  // Load all notes when modal opens
  useEffect(() => {
    console.log('NotesModal useEffect - isOpen:', isOpen, 'contactId:', contactId);
    if (isOpen && contactId) {
      loadAllNotes();
      if (openNewNoteForm) setShowNewNoteForm(true);
    }
    if (!isOpen) {
      setShowNewNoteForm(false);
      setIsNewNoteExpanded(false);
    }
  }, [isOpen, contactId]);

  const loadAllNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await axios.get(`/api/notes/contact/${contactId}`);

      if (response.data.success) {
        const fetchedNotes = (response.data.notes || []).map(enhanceNote);
        setNotes(sortNotesForDisplay(fetchedNotes));
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
      alert('Failed to load notes: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleClose = async () => {
    await cleanupTemporaryAttachments(newNoteAttachments);
    await cleanupTemporaryAttachments(editingAttachments);

    setNotes([]);
    setEditingNoteId(null);
    setEditingTitle('');
    setEditingContent('');
    setEditingAttachments([]);
    setEditingRemovedAttachmentIds([]);
    setNewNoteTitle('');
    setNewNoteContent('');
    setNewNoteAttachments([]);
    setShowNewNoteForm(false);
    setIsNewNoteExpanded(false);
    onClose();
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingTitle(note.title || '');
    setEditingContent(note.content);
    const attachments = enhanceAttachments(note.attachments || []);
    setEditingAttachments(attachments);
    setEditingRemovedAttachmentIds([]);
  };

  const cancelEditNote = async () => {
    await cleanupTemporaryAttachments(editingAttachments);
    setEditingNoteId(null);
    setEditingTitle('');
    setEditingContent('');
    setEditingAttachments([]);
    setEditingRemovedAttachmentIds([]);
  };

  const saveEditNote = async (noteId) => {
    try {
      setLoading(true);
      const isLatest = notes[0]?.id === noteId;
      const response = await axios.put(`/api/notes/${noteId}`, {
        title: editingTitle,
        content: editingContent,
        attachments: editingAttachments
      });

      if (response.data.success && response.data.note) {
        const updatedNote = enhanceNote(response.data.note);

        setNotes(prev => sortNotesForDisplay(
          prev.map(note => (note.id === noteId ? updatedNote : note))
        ));

        if (isLatest) {
          onSave(contactId, contactType, updatedNote.content);
        }

        await deleteAttachmentFilesByIds(editingRemovedAttachmentIds);

        setEditingNoteId(null);
        setEditingTitle('');
        setEditingContent('');
        setEditingAttachments([]);
        setEditingRemovedAttachmentIds([]);
      }
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Failed to update note: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      setLoading(true);
      const noteToDelete = notes.find(note => note.id === noteId);
      const response = await axios.delete(`/api/notes/${noteId}`);

      if (response.data.success) {
        // Update local notes state
        const updatedNotes = sortNotesForDisplay(notes.filter(note => note.id !== noteId));
        setNotes(updatedNotes);

        // Update the parent component with the new latest note
        const newLatestNote = updatedNotes[0];
        onSave(contactId, contactType, newLatestNote ? newLatestNote.content : '');

        if (noteToDelete?.attachments?.length) {
          await deleteAttachmentFiles(noteToDelete.attachments);
        }
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Failed to delete note: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const createNewNote = async () => {
    if (!newNoteContent.trim()) return;

    try {
      setLoading(true);
      const response = await axios.post('/api/notes', {
        contact_id: contactId,
        title: newNoteTitle,
        content: newNoteContent.trim(),
        source: 'manual',
        attachments: newNoteAttachments
      });

      if (response.data.success && response.data.note) {
        const createdNote = enhanceNote(response.data.note);
        // Add new note to the beginning of the list (most recent first)
        setNotes(prev => sortNotesForDisplay([createdNote, ...prev]));

        // Update the parent component with the new latest note
        onSave(contactId, contactType, createdNote.content);

        // Reset form
        setNewNoteTitle('');
        setNewNoteContent('');
        setNewNoteAttachments([]);
        setShowNewNoteForm(false);
        setIsNewNoteExpanded(false);
      }
    } catch (error) {
      console.error('Failed to create note:', error);
      alert('Failed to create note: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4">
      <div className={`bg-white rounded-lg p-4 sm:p-6 w-full flex flex-col transition-all duration-200 ${
        isNewNoteExpanded
          ? 'max-w-5xl h-[95vh] sm:h-[90vh]'
          : 'max-w-2xl max-h-[90vh]'
      }`}>
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">Notes</h3>
            {!showNewNoteForm && (
              <button
                onClick={() => {
                  setShowNewNoteForm(true);
                  setIsNewNoteExpanded(false);
                }}
                className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 border border-blue-600 hover:text-blue-800 hover:bg-blue-50 hover:border-blue-800 rounded"
                disabled={loading}
              >
                <Plus size={16} />
                New Note
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className={`min-h-0 overflow-y-auto ${isNewNoteExpanded ? 'flex-1' : ''}`}>
          {loadingNotes ? (
            <div className="text-center py-8">
              <div className="text-gray-600">Loading notes...</div>
            </div>
          ) : (
            <>
            {/* Existing Notes */}
            <div className={`${isNewNoteExpanded ? 'hidden' : 'space-y-3 mb-4'}`}>
              {notes.length === 0 ? (
                <div className="text-gray-500 text-center py-8">
                  No notes yet. Click "Add Note" to create the first one.
                </div>
              ) : (
                notes.map((note) => (
                  <div key={note.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm text-gray-500">
                        {formatDate(note.created_at)}
                        {note.source && ` • ${note.source}`}
                        {note.updated_at !== note.created_at && ' • edited'}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditNote(note)}
                          className="text-gray-400 hover:text-blue-600"
                          disabled={loading || editingNoteId === note.id}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="text-gray-400 hover:text-red-600"
                          disabled={loading}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {editingNoteId === note.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Title (optional)"
                        />
                        <textarea
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="mt-3">
                          <FileAttachmentSection
                            attachments={editingAttachments}
                            onFileSelect={handleEditFileSelect}
                            onRemoveAttachment={handleRemoveEditingAttachment}
                            uploadingFile={uploadingEditAttachments}
                            label="Attachments"
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                          <button
                            onClick={cancelEditNote}
                            className="px-3 py-1 text-gray-600 hover:text-gray-800"
                            disabled={loading}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => saveEditNote(note.id)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            disabled={loading || !editingContent.trim()}
                          >
                            {loading ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {note.title && (
                          <div className="text-base font-semibold text-gray-900 mb-1">
                            {note.title}
                          </div>
                        )}
                        <div className="text-gray-900 whitespace-pre-wrap">
                          {normalizeNoteContent(note.content)}
                        </div>
                        {note.attachments?.length > 0 && (
                          <div className="mt-3 border border-gray-200 rounded-lg bg-white p-3">
                            <p className="text-xs font-semibold text-gray-600 mb-2">
                              Attachments
                            </p>
                            <div className="space-y-1">
                              {note.attachments.map(attachment => (
                                <a
                                  key={attachment.id}
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between text-sm text-blue-600 hover:underline"
                                >
                                  <span className="truncate">📎 {attachment.originalName || attachment.filename}</span>
                                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                    {fileAttachmentUtils.formatFileSize(attachment.size || 0)}
                                  </span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Add New Note Section */}
            {showNewNoteForm && (
              <div className={`border border-gray-200 rounded-lg p-4 bg-blue-50 ${
                isNewNoteExpanded ? 'h-full flex flex-col' : ''
              }`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h4 className="font-medium">Add New Note</h4>
                  <button
                    type="button"
                    onClick={() => setIsNewNoteExpanded(value => !value)}
                    className="p-1.5 text-gray-500 hover:text-blue-700 hover:bg-blue-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label={isNewNoteExpanded ? 'Restore note editor size' : 'Expand note editor'}
                    title={isNewNoteExpanded ? 'Restore editor size' : 'Expand editor'}
                  >
                    {isNewNoteExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                  </button>
                </div>
                <input
                  type="text"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Title (optional)"
                />
                <textarea
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  onKeyDown={(event) => {
                    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                      event.preventDefault();
                      if (!loading && newNoteContent.trim()) createNewNote();
                    }
                  }}
                  className={`w-full min-h-36 p-3 border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    isNewNoteExpanded ? 'flex-1 sm:min-h-64' : ''
                  }`}
                  placeholder="Enter your note..."
                />
                <div className="mt-3">
                  <FileAttachmentSection
                    attachments={newNoteAttachments}
                    onFileSelect={handleNewNoteFileSelect}
                    onRemoveAttachment={handleRemoveNewNoteAttachment}
                    uploadingFile={uploadingNewAttachments}
                    label="Attachments"
                  />
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 mt-4 flex-shrink-0">
                  <span className="text-xs text-gray-500 mr-auto hidden sm:inline">
                    Ctrl/⌘+Enter to add note
                  </span>
                  <button
                    onClick={cancelNewNoteForm}
                    className="px-3 py-1 text-gray-600 hover:text-gray-800"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createNewNote}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    disabled={loading || !newNoteContent.trim()}
                  >
                    {loading ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotesModal;
